import {
    assembleConstellationDerivations,
    assembleItemDerivations,
    toEntityItemCollectionDerivation,
    toItemCollections,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { createFrameExtentPointCollector } from '@toolproof-core/visualization/rendering/frame-extent-point-collector';
import type {
    ItemParametricState,
} from '@toolproof-core/visualization/_contract/item';
import type { CollectionKey } from '@toolproof-core/visualization/_contract/key';
import type { SpaceDerivationFrame, SpaceDeriver } from '@toolproof-core/visualization/derivation/_contract/derivation';
import type {
    EntityItemCollectionDerivation,
    ItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toItemDerivation } from '@/workflow/_pure/derivation/itemFactories';
import {
    OVERVIEW_VIEW_KEY,
    withCameraInitializationKey,
    withOverviewView,
} from '@/workflow/_pure/derivation/overviewView';
import { resolveEffectiveGeometryConfig } from '@/workflow/geometryConfigResolver';
import type { BaseConfig } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type {
    WorkflowDerivationInput,
    WorkflowModeAugmentationController,
    WorkflowModeAugmentationInput,
} from '@/workflow/_contract/modeAugmentation';

function mergeItemParametricState(
    base: ItemParametricState | undefined,
    patch: ItemParametricState | undefined,
): ItemParametricState | undefined {
    if (base === undefined) return patch;
    if (patch === undefined) return base;

    return {
        ...(base.entityItemByKey === undefined && patch.entityItemByKey === undefined
            ? {}
            : {
                entityItemByKey: {
                    ...(base.entityItemByKey ?? {}),
                    ...(patch.entityItemByKey ?? {}),
                },
            }),
        ...(base.connectorItemByKey === undefined && patch.connectorItemByKey === undefined
            ? {}
            : {
                connectorItemByKey: {
                    ...(base.connectorItemByKey ?? {}),
                    ...(patch.connectorItemByKey ?? {}),
                },
            }),
    };
}

export function createSpaceDeriver<
    TConfig extends BaseConfig,
    TRuntimeState,
    TPipelineContext = unknown,
>(args: {
    resolveConfig: (input: WorkflowDerivationInput<TConfig, TRuntimeState, TPipelineContext>) => TConfig;
    resolveModeAugmentation: (
        input: WorkflowDerivationInput<TConfig, TRuntimeState, TPipelineContext>,
        config: TConfig,
    ) => WorkflowModeAugmentationController<TConfig, TRuntimeState, TPipelineContext>;
}): SpaceDeriver<CosmosDataModel, TConfig, TRuntimeState> {
    const collectOverviewPoints = createFrameExtentPointCollector();
    const deriveBaseFrame = (
        input: WorkflowDerivationInput<TConfig, TRuntimeState, TPipelineContext>,
    ) => {
        const config = resolveEffectiveGeometryConfig(args.resolveConfig(input));
        const modeAugmentationController = args.resolveModeAugmentation(input, config);
        const controllerArgs = {
            input: {
                cosmosData: input.cosmosData,
                runtimeState: input.runtimeState,
                ...(input.pipelineContext === undefined ? {} : { pipelineContext: input.pipelineContext }),
            } satisfies WorkflowModeAugmentationInput<TRuntimeState, TPipelineContext>,
            config,
        };
        const resourceCollections = modeAugmentationController.deriveResourceCollections(controllerArgs);
        const resourceBackedItemCollectionDerivations = resourceCollections.flatMap<EntityItemCollectionDerivation>((resourceCollection) => {
            const itemDerivations = resourceCollection.resources.map((resource) => toItemDerivation(resource, config));
            if (itemDerivations.length === 0) return [];

            return [toEntityItemCollectionDerivation({
                itemCollectionKey: `collection:entity:${resourceCollection.resourceCollectionKey}` as CollectionKey<'entity'>,
                itemDerivations,
            })];
        });
        const resourceBackedItemCollections = toItemCollections(resourceBackedItemCollectionDerivations);
        const syntheticItemCollectionDerivations = modeAugmentationController.deriveSyntheticItemCollectionDerivations({
            ...controllerArgs,
            itemCollections: resourceBackedItemCollections,
            itemCollectionDerivations: resourceBackedItemCollectionDerivations,
        });
        const itemCollectionDerivations: ItemCollectionDerivation[] = [
            ...resourceBackedItemCollectionDerivations,
            ...syntheticItemCollectionDerivations,
        ];
        const itemAssembly = assembleItemDerivations(itemCollectionDerivations);
        const itemCollections = itemAssembly.itemCollections;
        const constellationDerivations = modeAugmentationController.deriveConstellationDerivations({
            ...controllerArgs,
            itemCollections,
        });
        const constellationAssembly = assembleConstellationDerivations(constellationDerivations);
        const constellations = constellationAssembly.constellations;
        const spaceRenderOptions = modeAugmentationController.deriveSpaceRenderOptions({
            ...controllerArgs,
            itemCollections,
        });
        const itemParametricPatchState = modeAugmentationController.deriveItemParametricState({
            ...controllerArgs,
            itemCollections,
            constellations,
        });
        const itemParametricState = mergeItemParametricState(
            itemAssembly.itemParametricState,
            itemParametricPatchState,
        );
        const constellationParametricState = constellationAssembly.constellationParametricState;
        const itemBehaviorController = modeAugmentationController.deriveItemBehaviorController?.({
            ...controllerArgs,
            itemCollections,
            constellations,
        });

        const frame: SpaceDerivationFrame = {
            spaceKey: input.spaceKey,
            itemCollections,
            constellations,
            spaceRenderOptions,
            itemParametricState,
            itemBehaviorController,
            constellationParametricState,
        };
        // Bounds are derived after mode augmentation so rods and other added geometry stay in the overview.
        const augmentedFrame = modeAugmentationController.augmentBaseFrame?.({ frame, input, config }) ?? frame;
        const assembledFrame = withOverviewView(
            augmentedFrame,
            modeAugmentationController.deriveOverviewPadding?.({ config }),
            augmentedFrame.spaceRenderOptions?.camera?.graph?.views[OVERVIEW_VIEW_KEY] === undefined
                ? collectOverviewPoints(augmentedFrame) : undefined,
        );
        return withCameraInitializationKey(
            assembledFrame,
            modeAugmentationController.deriveCameraInitializationKey?.({ frame: assembledFrame, input, config }),
        );
    };

    return (input) => {
        const config = resolveEffectiveGeometryConfig(args.resolveConfig(input));
        const modeAugmentationController = args.resolveModeAugmentation(input, config);
        const pipelineContext = modeAugmentationController.derivePipelineContext?.({
            input: {
                cosmosData: input.cosmosData,
                runtimeState: input.runtimeState,
            },
            config,
        });
        const inputWithPipelineContext: WorkflowDerivationInput<TConfig, TRuntimeState, TPipelineContext> = {
            ...input,
            ...(pipelineContext === undefined ? {} : { pipelineContext }),
        };

        const base = deriveBaseFrame(inputWithPipelineContext);
        const transitionPhases = modeAugmentationController.deriveTransitionPhases?.({
            base,
            input: inputWithPipelineContext,
            config,
            deriveBaseFrame,
        });

        return {
            base,
            transitionPhases,
            guidance: modeAugmentationController.deriveGuidance?.({ frame: base, input: inputWithPipelineContext, config }),
        };
    };
}
